import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    IconButton,
    Grid,
    Slide,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import ChatIcon from "@mui/icons-material/Chat";
import { motion } from "framer-motion";

import axios from "axios";
import url from "../api/apisList";

// Define the Slide transition function
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const DemoRequestDialog = ({ open, onClose }) => {
    const [formData, setFormData] = useState({
        fullName: "",
        workEmail: "",
        companyName: "",
        role: "",
        requirements: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const validateField = (name, value) => {
        let error = "";
        if (name === "fullName" && !value) error = "Full name is required";
        if (name === "workEmail") {
            if (!value) error = "Email is required";
            else if (!/\S+@\S+\.\S+/.test(value)) error = "Please enter a valid work email";
        }
        if (name === "companyName" && !value) error = "Company name is required";
        if (name === "role" && !value) error = "Your role is required";
        if (name === "requirements" && !value) error = "Please share your requirements";

        setErrors(prev => ({ ...prev, [name]: error }));
        return !error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            validateField(name, value);
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        validateField(name, value);
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };


    const handleSend = async () => {
        const newErrors = {};
        let isValid = true;

        Object.keys(formData).forEach(key => {
            if (!validateField(key, formData[key])) {
                isValid = false;
            }
        });

        if (!isValid) {
            setSnackbar({
                open: true,
                message: "Please correct the errors in the form.",
                severity: "error",
            });
            return;
        }

        setLoading(true);

        try {
            // We must call our own backend because SendGrid blocks browser requests (CORS)
            const response = await axios.post(url("send_demo_request"), formData);

            if (response.data.success || response.status === 200) {
                setSnackbar({
                    open: true,
                    message: "Request sent successfully! We will contact you shortly.",
                    severity: "success",
                });
                setTimeout(() => {
                    onClose();
                    setFormData({
                        fullName: "",
                        workEmail: "",
                        companyName: "",
                        role: "",
                        requirements: "",
                    });
                }, 2000);
            } else {
                throw new Error("Failed to send email");
            }
        } catch (error) {
            console.error("Error sending email:", error);
            const errorMsg = error.response?.status === 404
                ? "Backend endpoint missing. Please see SERVER_INSTRUCTIONS.md"
                : "Failed to send request. Please try again later.";

            setSnackbar({
                open: true,
                message: errorMsg,
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                TransitionComponent={Transition}
                PaperProps={{
                    sx: {
                        borderRadius: "24px",
                        overflow: "hidden",
                        background: "#FFFFFF",
                        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                        width: "100%",
                        maxWidth: "800px",
                    },
                }}
            >
                <div style={{ position: "relative", overflow: "hidden" }}>
                    {/* Animated background shape */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: -100,
                            right: -100,
                            width: 300,
                            height: 300,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 145, 0, 0.05) 100%)",
                            zIndex: 0,
                        }}
                    />

                    <DialogTitle
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            pt: 4,
                            px: 4,
                            zIndex: 1,
                            position: "relative",
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "28px",
                                    fontWeight: 800,
                                    color: "#0F172A",
                                    fontFamily: "'Poppins', sans-serif",
                                    letterSpacing: "-0.5px",
                                }}
                            >
                                Request a Demo
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "15px",
                                    color: "#64748B",
                                    fontFamily: "'Poppins', sans-serif",
                                    mt: 0.5
                                }}
                            >
                                Experience the future of recruitment automation.
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: "#64748B",
                                bgcolor: "rgba(0,0,0,0.05)",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.1)" }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent sx={{ p: 4, pt: 2, zIndex: 1, position: "relative" }}>
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <motion.div variants={itemVariants}>
                                        <Typography sx={{ mb: 1, fontWeight: 500, color: "#1E293B", fontSize: "14px", fontFamily: "'Poppins', sans-serif" }}>
                                            Full Name
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!errors.fullName}
                                            helperText={errors.fullName}
                                            variant="outlined"
                                            placeholder="John Doe"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonIcon sx={{ color: errors.fullName ? "#d32f2f" : "#FF6B00", marginRight: 1 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                                                "& .MuiOutlinedInput-input": { py: 1.5 }
                                            }}
                                        />
                                    </motion.div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <motion.div variants={itemVariants}>
                                        <Typography sx={{ mb: 1, fontWeight: 500, color: "#1E293B", fontSize: "14px", fontFamily: "'Poppins', sans-serif" }}>
                                            Work Email
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="workEmail"
                                            value={formData.workEmail}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!errors.workEmail}
                                            helperText={errors.workEmail}
                                            variant="outlined"
                                            placeholder="john@company.com"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EmailIcon sx={{ color: errors.workEmail ? "#d32f2f" : "#FF6B00", marginRight: 1 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                                                "& .MuiOutlinedInput-input": { py: 1.5 }
                                            }}
                                        />
                                    </motion.div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <motion.div variants={itemVariants}>
                                        <Typography sx={{ mb: 1, fontWeight: 500, color: "#1E293B", fontSize: "14px", fontFamily: "'Poppins', sans-serif" }}>
                                            Company Name
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!errors.companyName}
                                            helperText={errors.companyName}
                                            variant="outlined"
                                            placeholder="Acme Inc."
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <BusinessIcon sx={{ color: errors.companyName ? "#d32f2f" : "#FF6B00", marginRight: 1 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                                                "& .MuiOutlinedInput-input": { py: 1.5 }
                                            }}
                                        />
                                    </motion.div>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <motion.div variants={itemVariants}>
                                        <Typography sx={{ mb: 1, fontWeight: 500, color: "#1E293B", fontSize: "14px", fontFamily: "'Poppins', sans-serif" }}>
                                            Your Role
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            name="role"
                                            value={formData.role}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!errors.role}
                                            helperText={errors.role}
                                            variant="outlined"
                                            placeholder="Technical Recruiter"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <WorkIcon sx={{ color: errors.role ? "#d32f2f" : "#FF6B00", marginRight: 1 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                                                "& .MuiOutlinedInput-input": { py: 1.5 }
                                            }}
                                        />
                                    </motion.div>
                                </Grid>
                                <Grid item xs={12}>
                                    <motion.div variants={itemVariants}>
                                        <Typography sx={{ mb: 1, fontWeight: 500, color: "#1E293B", fontSize: "14px", fontFamily: "'Poppins', sans-serif" }}>
                                            How can we help?
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            name="requirements"
                                            value={formData.requirements}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!errors.requirements}
                                            helperText={errors.requirements}
                                            variant="outlined"
                                            placeholder="Tell us about your requirements..."
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                                                        <ChatIcon sx={{ color: errors.requirements ? "#d32f2f" : "#FF6B00", marginRight: 1 }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                                        />
                                    </motion.div>
                                </Grid>
                                <Grid item xs={12}>
                                    <motion.div
                                        variants={itemVariants}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                    >
                                        <Button
                                            variant="contained"

                                            onClick={handleSend}
                                            disabled={loading}
                                            sx={{
                                                background: "linear-gradient(45deg, #FF6B00 0%, #FF9100 100%)",
                                                color: "white",
                                                borderRadius: "12px",
                                                py: 2,
                                                fontSize: "16px",
                                                float: "right",
                                                fontWeight: 500,
                                                textTransform: "none",
                                                boxShadow: "0 10px 20px rgba(255, 107, 0, 0.3)",
                                                "&:hover": {
                                                    background: "linear-gradient(45deg, #E65100 0%, #FF6B00 100%)",
                                                    boxShadow: "0 15px 25px rgba(255, 107, 0, 0.4)",
                                                },
                                                transition: "all 0.4s ease",
                                            }}
                                        >
                                            {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Request"}
                                        </Button>
                                    </motion.div>
                                </Grid>
                            </Grid>
                        </motion.div>
                    </DialogContent>
                </div>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        width: "100%",
                        borderRadius: "12px",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)"
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default DemoRequestDialog;
